import i18n from '@risksmart-app/i18n/src/i18n';
import type { IndicatorRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  GetIndicatorsQuery,
  Indicator_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetIndicatorsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { mapTrpcIndicatorsToGraphQL } from 'src/hooks/queries/indicator/useGetIndicatorRegister';
import { useGetObligationsRegister } from 'src/hooks/queries/obligation/useGetObligationsRegister';
import { useGetLabelledFields as useObligationLabelledFields } from 'src/pages/compliance/obligations/useLabelledFields';
import { useLabelledFields as useIndicatorLabelledFields } from 'src/pages/indicators/useLabelledFields';
import type { LabelledIdArray } from 'src/rbac/types';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { TableFields, TableRecord } from '@/utils/table/types';

import { UNRATED } from '../../gigawidget/types';
import { createDataSource } from '../createDataSource';
import { defaultClickthroughFilter } from '../dataSourceHelpers';
import {
  type IndicatorRowSlice,
  indicatorSideCategoryGetters,
  indicatorSideFieldConfig,
  ownersField,
  prefixedLabel,
  resolveIndicatorSideRow,
  simpleField,
  useRelationshipTableProps,
} from './relationship-helpers';

export type IndicatorObligationRow = TableRecord &
  IndicatorRowSlice & {
    Id: string;
    ObligationId: string;
    ObligationFriendlyId: string;
    ObligationTitle: string;
    ObligationType: string;
    // Obligations don't have a lifecycle Status column; product (RSP-5307)
    // confirmed Adherence is the intended semantic, so the column is surfaced
    // as "Obligation Adherence" sourced from `AdherenceLabel`.
    ObligationAdherence: string;
    ObligationOwners: LabelledIdArray;
    ObligationContributors: LabelledIdArray;
  };

export default createDataSource({
  group: 'relationship',
  hasAccess: () => true,
  parentTypes: [Parent_Type_Enum.Obligation, Parent_Type_Enum.Indicator],
  documentNode: GetIndicatorsDocument,
  trpcQuery: (trpc) => {
    const queryOptions = trpc.frontend.indicator.register.queryOptions();
    const transformedQueryOptions = {
      ...queryOptions,
      select: (data: IndicatorRegisterResponse) =>
        mapTrpcIndicatorsToGraphQL(data),
    };

    return transformedQueryOptions as UseQueryOptions<unknown, Error>;
  },
  useDefaultVariables: () => ({
    where: useEntityWhereFilter<Indicator_Bool_Exp>(Parent_Type_Enum.Indicator),
  }),
  useTablePropsHook: (
    data: GetIndicatorsQuery | undefined,
    options: StatefulTableOptions<IndicatorObligationRow>
  ) => {
    const fields = useMemo<TableFields<IndicatorObligationRow>>(
      () => ({
        ObligationFriendlyId: simpleField(
          prefixedLabel('obligation_one', i18n.t('columns.id'))
        ),
        ObligationTitle: simpleField(
          prefixedLabel('obligation_one', i18n.t('obligations.columns.Title'))
        ),
        ObligationType: simpleField(
          prefixedLabel('obligation_one', i18n.t('obligations.columns.Type'))
        ),
        ObligationAdherence: simpleField(
          prefixedLabel(
            'obligation_one',
            i18n.t('obligations.columns.Adherence')
          )
        ),
        ObligationOwners: ownersField(
          prefixedLabel('obligation_one', i18n.t('obligations.columns.Owner')),
          (item) => item.ObligationOwners
        ),
        ObligationContributors: ownersField(
          prefixedLabel(
            'obligation_one',
            i18n.t('obligations.columns.Contributor')
          ),
          (item) => item.ObligationContributors
        ),
        ...indicatorSideFieldConfig<IndicatorObligationRow>(),
      }),
      []
    );

    const indicatorsLabelled = useIndicatorLabelledFields(data?.indicator);

    const { data: obligationData } = useGetObligationsRegister({
      queryArgs: {},
    });
    const latestAssessmentResults = useMemo(
      () =>
        obligationData?.assessment_result_parent.map(
          (ar) => ar.obligationAssessmentResult
        ),
      [obligationData?.assessment_result_parent]
    );
    const obligationsLabelled = useObligationLabelledFields(
      obligationData?.obligation,
      latestAssessmentResults
    );

    const joinedData = useMemo<IndicatorObligationRow[] | undefined>(() => {
      if (!indicatorsLabelled || !obligationsLabelled) {
        return undefined;
      }

      const obligationMap = new Map(obligationsLabelled.map((o) => [o.Id, o]));

      return indicatorsLabelled.flatMap((indicator) =>
        (indicator.parents ?? [])
          .filter(
            (p) =>
              p.parent?.ObjectType === Parent_Type_Enum.Obligation &&
              p.parent.Id
          )
          .map((parent) => {
            const obligation = obligationMap.get(parent.parent!.Id);

            return {
              Id: `${parent.parent!.Id}_${indicator.Id}`,
              ObligationId: parent.parent!.Id,
              ObligationFriendlyId: obligation?.SequentialIdLabel ?? '',
              // Partner title falls back to '' (see note in indicatorRisk.ts).
              ObligationTitle: obligation?.Title ?? '',
              ObligationType: obligation?.TypeLabel ?? '',
              ObligationAdherence: obligation?.AdherenceLabel ?? '',
              ObligationOwners: obligation?.allOwners ?? [],
              ObligationContributors: obligation?.allContributors ?? [],
              ...resolveIndicatorSideRow(indicator),
            };
          })
      );
    }, [indicatorsLabelled, obligationsLabelled]);

    return useRelationshipTableProps(
      joinedData,
      fields,
      i18n.t('obligation_indicator_other'),
      options
    );
  },
  entityNamePlural: 'obligation_indicator_other',
  entityNameSingular: 'obligation_indicator_one',
  fields: 'obligationIndicators.fields',
  dashboardFilterConfig: {},
  categoryGetters: [
    {
      id: 'obligationFriendlyId',
      entityGroup: 'obligation_one',
      name: () => prefixedLabel('obligation_one', i18n.t('columns.id')),
      categoryGetter: (item: IndicatorObligationRow) =>
        item.ObligationFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ObligationFriendlyId'),
    },
    {
      id: 'obligationTitle',
      entityGroup: 'obligation_one',
      name: () =>
        prefixedLabel('obligation_one', i18n.t('obligations.columns.Title')),
      categoryGetter: (item: IndicatorObligationRow) =>
        item.ObligationTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ObligationTitle'),
    },
    {
      id: 'obligationType',
      entityGroup: 'obligation_one',
      name: () =>
        prefixedLabel('obligation_one', i18n.t('obligations.columns.Type')),
      categoryGetter: (item: IndicatorObligationRow) =>
        item.ObligationType || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ObligationType'),
    },
    {
      id: 'obligationAdherence',
      entityGroup: 'obligation_one',
      name: () =>
        prefixedLabel(
          'obligation_one',
          i18n.t('obligations.columns.Adherence')
        ),
      categoryGetter: (item: IndicatorObligationRow) =>
        item.ObligationAdherence || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ObligationAdherence'),
    },
    {
      id: 'obligationOwner',
      entityGroup: 'obligation_one',
      name: () =>
        prefixedLabel('obligation_one', i18n.t('obligations.columns.Owner')),
      categoryGetter: (item: IndicatorObligationRow) =>
        item.ObligationOwners.map((owner) => ({
          key: owner.id,
          label: owner.label,
        })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'ObligationOwners',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'obligationContributor',
      entityGroup: 'obligation_one',
      name: () =>
        prefixedLabel(
          'obligation_one',
          i18n.t('obligations.columns.Contributor')
        ),
      categoryGetter: (item: IndicatorObligationRow) =>
        item.ObligationContributors.map((contributor) => ({
          key: contributor.id,
          label: contributor.label,
        })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'ObligationContributors',
          operator: '=',
          value: category.key,
        },
      ],
    },
    ...indicatorSideCategoryGetters<IndicatorObligationRow>(),
  ],
});
